/**
 * Calendar URL helpers — BW10. We use the following segment formats:
 *   day:   YYYY-MM-DD                   e.g. 2026-04-30
 *   week:  YYYY-Www  (ISO 8601 week)   e.g. 2026-W18
 *   month: YYYY-MM                      e.g. 2026-04
 *   year:  YYYY                         e.g. 2026
 *
 * Week numbering follows ISO 8601: Mondays anchor weeks, week 1 contains Jan 4.
 */

export function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function isoMonth(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

export function isoYear(d: Date): string {
  return String(d.getFullYear());
}

export function isoWeek(d: Date): string {
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  // ISO weekday Monday=1 … Sunday=7
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${tmp.getUTCFullYear()}-W${String(weekNum).padStart(2, "0")}`;
}

/** Parse a `YYYY-Www` string into the Date for the Monday of that ISO week. */
export function parseIsoWeek(s: string): Date {
  const m = /^(\d{4})-W(\d{2})$/.exec(s);
  if (!m) return startOfWeek(new Date());
  const year = Number(m[1]);
  const week = Number(m[2]);
  // ISO 8601: week 1 is the week containing the first Thursday.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  // Monday of week 1
  const week1Mon = new Date(jan4);
  week1Mon.setUTCDate(jan4.getUTCDate() - (jan4Day - 1));
  // Add (week-1) weeks
  week1Mon.setUTCDate(week1Mon.getUTCDate() + (week - 1) * 7);
  return new Date(week1Mon.getUTCFullYear(), week1Mon.getUTCMonth(), week1Mon.getUTCDate());
}

export function parseIsoMonth(s: string): Date {
  const m = /^(\d{4})-(\d{2})$/.exec(s);
  if (!m) return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  return new Date(Number(m[1]), Number(m[2]) - 1, 1);
}

export function parseIsoYear(s: string): Date {
  const m = /^(\d{4})$/.exec(s);
  if (!m) return new Date(new Date().getFullYear(), 0, 1);
  return new Date(Number(m[1]), 0, 1);
}

/**
 * Locale-aware start of week. Defaults to Monday for backwards-compat with
 * the BW10 ISO-week URL scheme. Pass `weekStartsOn` (0=Sun, 1=Mon, 6=Sat) to
 * honor a user setting — see `useWeekStart()`.
 */
export function startOfWeek(d: Date, weekStartsOn: 0 | 1 | 6 = 1): Date {
  const day = d.getDay(); // Sun=0…Sat=6
  // How many days back to walk to land on weekStartsOn.
  const offset = (day - weekStartsOn + 7) % 7;
  const result = new Date(d);
  result.setHours(0, 0, 0, 0);
  result.setDate(result.getDate() - offset);
  return result;
}

/**
 * Days-of-week order for a calendar header, given the chosen start day.
 * Returns short labels Mon/Tue/… in the right rotation.
 */
export function weekDayHeaders(weekStartsOn: 0 | 1 | 6): string[] {
  const base = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return [...base.slice(weekStartsOn), ...base.slice(0, weekStartsOn)];
}

export function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

export function addYears(d: Date, n: number): Date {
  return new Date(d.getFullYear() + n, 0, 1);
}
